/**
 * Validation utilities for TextEditor components
 * Provides error handling and validation for scale, rotation, colors, and fonts
 */

// Validation constants
export const VALIDATION_CONSTANTS = {
  SCALE: {
    MIN: 1,
    MAX: 50,
    DEFAULT: 24,
  },
  ROTATE: {
    MIN: 0,
    MAX: 360,
    DEFAULT: 0,
  },
  COLOR: {
    DEFAULT: '#000000',
    HEX_PATTERN: /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/,
  },
  FONT: {
    DEFAULT: 'Basic',
    FALLBACK_FONTS: ['Arial', 'Helvetica', 'sans-serif'],
  },
};

/**
 * Validates and clamps scale values between 1-50
 * @param {number} value - Scale value to validate
 * @param {number} defaultValue - Default value to use if invalid
 * @returns {number} Clamped scale value
 */
export const validateScale = (value, defaultValue = VALIDATION_CONSTANTS.SCALE.DEFAULT) => {
  try {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(numValue) || !isFinite(numValue)) {
      console.warn(`Invalid scale value: ${value}, using default: ${defaultValue}`);
      return defaultValue;
    }

    const clampedValue = Math.max(
      VALIDATION_CONSTANTS.SCALE.MIN,
      Math.min(VALIDATION_CONSTANTS.SCALE.MAX, numValue),
    );

    if (clampedValue !== numValue) {
      console.warn(`Scale value ${numValue} clamped to ${clampedValue}`);
    }

    return clampedValue;
  } catch (error) {
    console.error('Error validating scale value:', error);
    return defaultValue;
  }
};

/**
 * Validates and normalizes rotation values between 0-360
 * @param {number} value - Rotation value to validate
 * @param {number} defaultValue - Default value to use if invalid
 * @returns {number} Normalized rotation value
 */
export const validateRotation = (value, defaultValue = VALIDATION_CONSTANTS.ROTATE.DEFAULT) => {
  try {
    // Convert to number if string
    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    // Check if it's a valid number
    if (isNaN(numValue) || !isFinite(numValue)) {
      console.warn(`Invalid rotation value: ${value}, using default: ${defaultValue}`);
      return defaultValue;
    }

    // Normalize to 0-360 range (handle negative values and values > 360)
    let normalizedValue = numValue % 360;
    if (normalizedValue < 0) {
      normalizedValue += 360;
    }

    // Log if value was normalized
    if (normalizedValue !== numValue) {
      console.warn(`Rotation value ${numValue} normalized to ${normalizedValue}`);
    }

    return normalizedValue;
  } catch (error) {
    console.error('Error validating rotation value:', error);
    return defaultValue;
  }
};

/**
 * Validates hex color format and provides fallback
 * @param {string} color - Color value to validate
 * @param {string} defaultColor - Default color to use if invalid
 * @returns {string} Valid hex color
 */
export const validateColor = (color, defaultColor = VALIDATION_CONSTANTS.COLOR.DEFAULT) => {
  try {
    // Check if color is a string
    if (typeof color !== 'string') {
      console.warn(`Invalid color type: ${typeof color}, using default: ${defaultColor}`);
      return defaultColor;
    }

    // Trim whitespace
    const trimmedColor = color.trim();

    // Check if it matches hex pattern
    if (!VALIDATION_CONSTANTS.COLOR.HEX_PATTERN.test(trimmedColor)) {
      console.warn(`Invalid color format: ${color}, using default: ${defaultColor}`);
      return defaultColor;
    }

    // Convert 3-digit hex to 6-digit hex
    if (trimmedColor.length === 4) {
      const shortHex = trimmedColor.slice(1);
      const longHex = shortHex
        .split('')
        .map((char) => char + char)
        .join('');
      return `#${longHex}`;
    }

    // Return uppercase hex color
    return trimmedColor.toUpperCase();
  } catch (error) {
    console.error('Error validating color value:', error);
    return defaultColor;
  }
};

/**
 * Validates font family and provides fallback
 * @param {string} font - Font family to validate
 * @param {Array} availableFonts - Array of available fonts
 * @param {string} defaultFont - Default font to use if invalid
 * @returns {string} Valid font family
 */
export const validateFont = (
  font,
  availableFonts = [],
  defaultFont = VALIDATION_CONSTANTS.FONT.DEFAULT,
) => {
  try {
    // Check if font is a string
    if (typeof font !== 'string') {
      console.warn(`Invalid font type: ${typeof font}, using default: ${defaultFont}`);
      return defaultFont;
    }

    // Trim whitespace
    const trimmedFont = font.trim();

    // Check if font is in available fonts list
    if (availableFonts.length > 0 && !availableFonts.includes(trimmedFont)) {
      console.warn(`Font not available: ${font}, using default: ${defaultFont}`);
      return defaultFont;
    }

    return trimmedFont;
  } catch (error) {
    console.error('Error validating font value:', error);
    return defaultFont;
  }
};

/**
 * Validates component height constraints
 * @param {number} height - Height value to validate
 * @param {number} maxHeight - Maximum allowed height
 * @returns {boolean} Whether height is within constraints
 */
export const validateHeightConstraint = (height, maxHeight = 130) => {
  try {
    const numHeight = typeof height === 'string' ? parseFloat(height) : height;

    if (isNaN(numHeight) || !isFinite(numHeight)) {
      console.warn(`Invalid height value: ${height}`);
      return false;
    }

    const isValid = numHeight <= maxHeight;

    if (!isValid) {
      console.warn(`Height constraint violation: ${numHeight}px exceeds maximum ${maxHeight}px`);
    }

    return isValid;
  } catch (error) {
    console.error('Error validating height constraint:', error);
    return false;
  }
};

/**
 * Safely executes a function with error handling
 * @param {Function} fn - Function to execute
 * @param {*} fallbackValue - Value to return if function fails
 * @param {string} context - Context description for error logging
 * @returns {*} Function result or fallback value
 */
export const safeExecute = (fn, fallbackValue = null, context = 'unknown') => {
  try {
    return fn();
  } catch (error) {
    console.error(`Error in ${context}:`, error);
    return fallbackValue;
  }
};

/**
 * Validates and sanitizes component props
 * @param {Object} props - Props object to validate
 * @param {Object} schema - Validation schema
 * @returns {Object} Sanitized props
 */
export const validateProps = (props, schema) => {
  try {
    const sanitizedProps = { ...props };

    Object.keys(schema).forEach((key) => {
      const validator = schema[key];
      if (typeof validator === 'function' && props.hasOwnProperty(key)) {
        sanitizedProps[key] = validator(props[key]);
      }
    });

    return sanitizedProps;
  } catch (error) {
    console.error('Error validating props:', error);
    return props;
  }
};

/**
 * Creates a debounced validation function
 * @param {Function} validator - Validation function
 * @param {number} delay - Debounce delay in milliseconds
 * @returns {Function} Debounced validator
 */
export const createDebouncedValidator = (validator, delay = 300) => {
  let timeoutId;

  return (...args) => {
    clearTimeout(timeoutId);

    return new Promise((resolve) => {
      timeoutId = setTimeout(() => {
        try {
          const result = validator(...args);
          resolve(result);
        } catch (error) {
          console.error('Error in debounced validator:', error);
          resolve(null);
        }
      }, delay);
    });
  };
};

/**
 * Font loading validation with fallback
 * @param {string} fontFamily - Font family to check
 * @returns {Promise<boolean>} Whether font loaded successfully
 */
export const validateFontLoading = async (fontFamily) => {
  try {
    // Check if font is available using CSS Font Loading API
    if ('fonts' in document) {
      const font = new FontFace(fontFamily, `url(data:,)`);
      await font.load();
      return document.fonts.check(`12px "${fontFamily}"`);
    }

    // Fallback method: create test element
    const testElement = document.createElement('div');
    testElement.style.fontFamily = fontFamily;
    testElement.style.fontSize = '12px';
    testElement.style.position = 'absolute';
    testElement.style.visibility = 'hidden';
    testElement.textContent = 'Test';

    document.body.appendChild(testElement);
    const computedFont = window.getComputedStyle(testElement).fontFamily;
    document.body.removeChild(testElement);

    return computedFont.includes(fontFamily);
  } catch (error) {
    console.warn(`Font loading validation failed for ${fontFamily}:`, error);
    return false;
  }
};

/**
 * Error boundary utility for component error handling
 * @param {Function} component - Component function to wrap
 * @param {*} fallback - Fallback content if component fails
 * @returns {Function} Error-wrapped component
 */
export const withErrorBoundary = (component, fallback = null) => {
  return (props) => {
    try {
      return component(props);
    } catch (error) {
      console.error('Component error:', error);
      return fallback;
    }
  };
};
